/**
 * Sector route registration — composition shell wires URL params/search to
 * SectorRoute. router.tsx lists this route next to area, dashboard, and stock.
 */
import { createRoute, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, type JSX } from 'react';
import { z } from 'zod';

import {
  SectorIdSchema,
  SectorSearchParamsSchema,
  type SectorSearchParams,
  type Ticker,
} from '@meridian/contracts';

import { Skeleton } from '@/components/primitives/skeleton';
import { stockTarget } from '@/lib/nav/targets';
import { rootRoute } from '@/router-root';

import { InvalidSectorParamError } from './invalid-sector-param-error';
import { SectorRouteError } from './sector-route-error';

const loadSectorRoute = () => import('./sector-route');

const SectorScreen = lazy(async () => {
  const module = await loadSectorRoute();
  return { default: module.SectorRoute };
});

const paramsSchema = z.object({
  sectorId: SectorIdSchema,
});

/** Stock hop — GICS-only; does not forward tax or area (AD-9). */
export function stockDetailTarget(
  ticker: Ticker,
  search: Pick<SectorSearchParams, 'win' | 'idx' | 'book' | 'cmp'>,
): ReturnType<typeof stockTarget> {
  return stockTarget(ticker, search);
}

function SectorRouteAdapter(): JSX.Element {
  const { sectorId } = sectorRoute.useParams();
  const search = sectorRoute.useSearch();
  const navigate = useNavigate();

  const onTickerSelect = useCallback(
    (ticker: Ticker) => {
      void navigate(
        stockDetailTarget(ticker, {
          win: search.win,
          idx: search.idx,
          book: search.book,
          cmp: search.cmp,
        }),
      );
    },
    [navigate, search.win, search.idx, search.book, search.cmp],
  );

  return (
    <Suspense fallback={<Skeleton height="18rem" label="Loading sector" />}>
      <SectorScreen
        sectorId={sectorId}
        window={search.win}
        index={search.idx}
        book={search.book}
        compare={search.cmp}
        onTickerSelect={onTickerSelect}
      />
    </Suspense>
  );
}

export const sectorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sector/$sectorId',
  params: {
    parse: (raw) => {
      const result = paramsSchema.safeParse(raw);
      if (!result.success) {
        /* Carries the sentinel the error boundary discriminates on. Throwing the
           raw ZodError instead is what let the router's default fallback render
           the whole internal sector-id enum as page content. */
        throw new InvalidSectorParamError();
      }
      return result.data;
    },
    stringify: (p) => ({ sectorId: p.sectorId }),
  },
  validateSearch: (search: Record<string, unknown>) => SectorSearchParamsSchema.parse(search),
  loader: loadSectorRoute,
  component: SectorRouteAdapter,
  errorComponent: SectorRouteError,
});
